import {
  DraftStringInput,
  DraftState,
  InlineMarkupType,
  MessageFormatType,
  StringVariant,
} from '../types';

const DRAFT_SCHEMA_VERSION = 1;

function buildKey(namespaceId: string, stringGroupId: string): string {
  return `langhub-draft:${namespaceId}:${stringGroupId}`;
}

/**
 * Reads a persisted draft from localStorage. Returns null if no draft exists
 * or if the stored data is corrupt / unparseable.
 */
export function getDraft(
  namespaceId: string,
  stringGroupId: string,
): DraftState | null {
  try {
    const raw = localStorage.getItem(buildKey(namespaceId, stringGroupId));
    if (!raw) {
      return null;
    }
    const parsed: DraftState = JSON.parse(raw);
    // Reject drafts from an incompatible schema version
    if (parsed.version !== DRAFT_SCHEMA_VERSION) {
      return null;
    }
    return parsed;
  } catch {
    // Corrupt data — treat as no draft
    return null;
  }
}

/**
 * Writes the draft to localStorage, propagating any failure.
 *
 * `setItem` throws when the quota is exceeded or writes are disabled (Safari
 * private mode). Callers that can surface that to the user must go through this
 * rather than {@link saveDraft}, so the failure is not mistaken for a save.
 */
function writeDraft(
  namespaceId: string,
  stringGroupId: string,
  draft: DraftState,
): void {
  localStorage.setItem(
    buildKey(namespaceId, stringGroupId),
    JSON.stringify(draft),
  );
}

/**
 * Persists the given draft state to localStorage. Persistence is best-effort:
 * `setItem` can throw (quota exceeded, or Safari private mode where writes are
 * disabled) and this is called from a render effect, so a throw here would
 * break the component render.
 *
 * Only for callers that have no way to tell the user — a render effect that
 * re-runs on the next change. A caller acting on an explicit Save, which then
 * discards the user's only copy of the edit, must use {@link persistFieldEdit}
 * and handle its throw instead.
 */
export function saveDraft(
  namespaceId: string,
  stringGroupId: string,
  draft: DraftState,
): void {
  try {
    writeDraft(namespaceId, stringGroupId, draft);
  } catch {
    // Storage unavailable or quota exceeded — drafts stay in memory only.
  }
}

/**
 * Removes the draft entry from localStorage.
 */
export function clearDraft(namespaceId: string, stringGroupId: string): void {
  localStorage.removeItem(buildKey(namespaceId, stringGroupId));
}

/**
 * Builds an empty changeset pinned to the server version it was branched from.
 * `baseVersion` is what the submit flow sends as the optimistic-concurrency
 * token, so it must be the version the first edit was made against.
 */
export function createEmptyDraft(baseVersion: number): DraftState {
  return {
    version: DRAFT_SCHEMA_VERSION,
    baseVersion,
    createdAt: Date.now(),
    added: [],
    edited: [],
    deleted: [],
  };
}

/**
 * Applies one string edit to a changeset, returning a new draft.
 *
 * This is the FULL-FORM path: `edited` describes the complete post-edit state of
 * the string, so an existing entry for the same string is replaced outright.
 * That is correct for the string-group page's Edit String modal, whose form is
 * seeded from the draft entry itself and therefore already carries every pending
 * field. Callers that know only the one field the user touched must use
 * {@link applyFieldEdit} instead, or they would drop the draft's other pending
 * values.
 *
 * A string created in this same draft session is updated in place inside
 * `added` — promoting it to `edited` would make the submit flow send an edit
 * for a string the server has never seen. Anything else is upserted into
 * `edited` so repeated edits of the same string collapse to one entry.
 */
export function applyStringEdit(
  draft: DraftState,
  edited: DraftStringInput,
): DraftState {
  const addedIdx = draft.added.findIndex((a) => a.stringId === edited.stringId);
  if (addedIdx !== -1) {
    const updatedAdded = [...draft.added];
    updatedAdded[addedIdx] = edited;
    return { ...draft, added: updatedAdded };
  }

  const editedIdx = draft.edited.findIndex(
    (e) => e.stringId === edited.stringId,
  );
  if (editedIdx !== -1) {
    const updatedEdited = [...draft.edited];
    updatedEdited[editedIdx] = edited;
    return { ...draft, edited: updatedEdited };
  }

  return { ...draft, edited: [...draft.edited, edited] };
}

/**
 * The format choices an edit interaction may also carry.
 *
 * Both are **string-level** in TRANSCOM — a variant overrides source text, never
 * the format — which is why they sit on both members of {@link StringFieldEdit}
 * and are applied to the entry's own fields even when the change is a variant's.
 * This matches the string-group page's Edit String modal, which likewise edits
 * format only at string level.
 *
 * Optional because a caller that did not surface the controls must be able to
 * say so: an omitted value leaves the draft entry's existing format untouched
 * rather than resetting it to a default. An entry whose format is itself absent
 * (TRANSCOM UNKNOWN) therefore stays absent, which is what the string detail
 * page relies on to avoid inventing a format on a text-only edit.
 */
interface StringFormatEdit {
  inlineMarkupType?: InlineMarkupType;
  messageFormatType?: MessageFormatType;
}

/**
 * The single field group one edit interaction changed: either the base
 * text/note pair, or exactly one CX variant's text/note pair — each optionally
 * accompanied by the string-level format choices.
 *
 * Modelled as a union rather than a partial `DraftStringInput` so "the user did
 * not touch this field" cannot be confused with "the user cleared it".
 */
export type StringFieldEdit =
  | ({
      kind: 'base';
      sourceText: string;
      contextNote: string;
    } & StringFormatEdit)
  | ({
      kind: 'variant';
      cxId: string;
      sourceText: string;
      contextNote: string;
    } & StringFormatEdit);

/** Replaces one CX variant's text/note, appending it when it does not exist yet. */
function upsertVariant(
  variants: StringVariant[],
  updated: StringVariant,
): StringVariant[] {
  const index = variants.findIndex((v) => v.cxId === updated.cxId);
  if (index === -1) {
    return [...variants, updated];
  }
  const next = [...variants];
  next[index] = updated;
  return next;
}

/**
 * Applies the change's string-level format choices, if it carried any.
 *
 * Written as conditional spreads rather than `?? entry.x` so an omitted choice
 * leaves the key exactly as it was — including its absence — instead of writing
 * back a value the caller never expressed.
 */
function withFormatEdit(
  entry: DraftStringInput,
  change: StringFieldEdit,
): DraftStringInput {
  return {
    ...entry,
    ...(change.inlineMarkupType
      ? { inlineMarkupType: change.inlineMarkupType }
      : {}),
    ...(change.messageFormatType
      ? { messageFormatType: change.messageFormatType }
      : {}),
  };
}

/** Applies one field-level change on top of an existing draft entry. */
function withFieldEdit(
  entry: DraftStringInput,
  change: StringFieldEdit,
): DraftStringInput {
  const withFormat = withFormatEdit(entry, change);
  if (change.kind === 'base') {
    return {
      ...withFormat,
      sourceText: change.sourceText,
      contextNote: change.contextNote,
    };
  }
  return {
    ...withFormat,
    variants: upsertVariant(withFormat.variants, {
      cxId: change.cxId,
      sourceText: change.sourceText,
      contextNote: change.contextNote,
    }),
  };
}

/**
 * Applies a FIELD-LEVEL edit to a changeset, returning a new draft.
 *
 * Used by callers that know only the field the user just changed — the string
 * detail page, whose modal edits either the base pair or a single variant.
 * `serverSnapshot` supplies the rest of the string as the server reports it and
 * is used ONLY when the string has no entry in the draft yet: when an entry
 * already exists, the change is applied on top of THAT entry so pending edits
 * to the other fields (base pair, format types, sibling variants) survive.
 * Replacing the entry with server-derived values instead would silently discard
 * them. The one exception is a format choice the change itself carries — the
 * user just made it in the modal, so it supersedes the entry's pending value.
 *
 * A string created in this draft session is updated in place inside `added`,
 * for the same reason {@link applyStringEdit} does so.
 */
export function applyFieldEdit(
  draft: DraftState,
  serverSnapshot: DraftStringInput,
  change: StringFieldEdit,
): DraftState {
  const { stringId } = serverSnapshot;

  const addedIdx = draft.added.findIndex((a) => a.stringId === stringId);
  if (addedIdx !== -1) {
    const updatedAdded = [...draft.added];
    updatedAdded[addedIdx] = withFieldEdit(updatedAdded[addedIdx], change);
    return { ...draft, added: updatedAdded };
  }

  const editedIdx = draft.edited.findIndex((e) => e.stringId === stringId);
  const updatedEdited = [...draft.edited];
  if (editedIdx !== -1) {
    updatedEdited[editedIdx] = withFieldEdit(updatedEdited[editedIdx], change);
  } else {
    updatedEdited.push(withFieldEdit(serverSnapshot, change));
  }

  return {
    ...draft,
    edited: updatedEdited,
    // An edit supersedes a pending deletion of the same string. Submitting both
    // would be contradictory — the changeset would ask the server to delete and
    // to update one string — and the user's latest intent is to keep it, so the
    // deletion is dropped. (The string-group page cannot reach this state: its
    // table offers no Edit action on a row already marked deleted. The string
    // detail page can, because it is reached by URL and does not read the
    // draft's deletion list.)
    deleted: draft.deleted.filter((id) => id !== stringId),
  };
}

/**
 * Reads the stored draft, applies one field-level string edit, and writes it
 * back — the `useDraftState.editString` flow for callers that are not mounted
 * inside the string-group page. `serverVersion` only takes effect when no draft
 * exists yet; an existing draft keeps the `baseVersion` of its first edit.
 *
 * **Throws** when the write does not land (quota exceeded, or writes disabled in
 * Safari private mode). This is the STRICT counterpart to {@link saveDraft}'s
 * best-effort write, and deliberately so: its callers persist an explicit Save
 * whose only copy is the draft, so a swallowed failure would let them close the
 * form and navigate away from an edit that was never stored. They must catch
 * this and keep the form open. The group page is unaffected — it persists from a
 * render effect through {@link saveDraft}, which stays lenient.
 *
 * Returns the persisted draft so callers can assert on / reuse it.
 */
export function persistFieldEdit(
  namespaceId: string,
  stringGroupId: string,
  serverVersion: number,
  serverSnapshot: DraftStringInput,
  change: StringFieldEdit,
): DraftState {
  const existing = getDraft(namespaceId, stringGroupId);
  const next = applyFieldEdit(
    existing ?? createEmptyDraft(serverVersion),
    serverSnapshot,
    change,
  );
  writeDraft(namespaceId, stringGroupId, next);
  return next;
}

export { DRAFT_SCHEMA_VERSION };
